import LegalLayout, { LegalSection } from "@/pages/LegalLayout";
import Seo from "@/components/Seo";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testId="privacy-page">
      <Seo
        title="Privacy Policy — 5ads.ai"
        description="How 5ads.ai collects, uses and protects your data across social.5ads.ai and search.5ads.ai, including data deletion instructions."
        url="https://5ads.ai/privacy"
      />
      <LegalSection heading="1. Who we are">
        <p>
          5ads.ai ("we", "us") operates the 5ads.ai family of AI-powered advertising applications,
          including social.5ads.ai (Meta ads manager) and the upcoming search.5ads.ai (Google Ads manager).
          This Privacy Policy explains how we collect, use, and protect your information across 5ads.ai
          and its applications.
        </p>
      </LegalSection>

      <LegalSection heading="2. Information we collect">
        <p>Depending on how you use our services, we may collect:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Account information</strong> — name, email address, and profile details when you sign up.</li>
          <li><strong>Ad platform data</strong> — when you connect your Meta or Google ad accounts, we access campaigns, ad sets, ads, creatives, audiences, performance insights, and lead form submissions via the official platform APIs, strictly with your authorization.</li>
          <li><strong>Contact form data</strong> — name, email, and message when you contact us via 5ads.ai.</li>
          <li><strong>Usage data</strong> — basic technical information such as browser type and pages visited, used to improve our services.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. How we use your information">
        <ul className="list-disc pl-5 space-y-2">
          <li>To operate the AI features of our apps: analyzing performance, drafting creatives, copy and budget recommendations for your approval.</li>
          <li>To sync and display your campaigns, leads and insights inside your dashboard.</li>
          <li>To respond to your inquiries and provide support.</li>
          <li>To improve, secure and maintain our services.</li>
        </ul>
        <p>
          We never sell your personal data. Ad account data is used solely to provide the service to you
          and is never shared with other customers or third-party advertisers.
        </p>
      </LegalSection>

      <LegalSection heading="4. Data from Meta and Google">
        <p>
          Our applications access Meta Marketing API and Google Ads API data only after you explicitly
          grant permission through the official OAuth login flows. You can revoke this access at any time
          from your Facebook or Google account security settings, or by disconnecting the ad account inside
          the app. Our use of data received from these APIs adheres to each platform's developer policies,
          including the Meta Platform Terms and the Google API Services User Data Policy, including its
          Limited Use requirements.
        </p>
      </LegalSection>

      <LegalSection heading="5. Data retention">
        <p>
          We retain your data while your account is active. Synced ad platform data (insights, leads,
          creatives) is kept to power historical reporting inside the app. When your account is deleted,
          associated data is removed from our production systems within 30 days.
        </p>
      </LegalSection>

      <LegalSection heading="6. Data deletion instructions" id="data-deletion">
        <p>You can request deletion of your data at any time using any of these methods:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>In-app:</strong> log in to your 5ads.ai application (e.g. social.5ads.ai), open Settings → Account → Delete account &amp; data.</li>
          <li><strong>By email:</strong> send a deletion request to <a href="mailto:privacy@5ads.ai" className="text-[#FF4D00] underline underline-offset-2">privacy@5ads.ai</a> from the email address associated with your account.</li>
          <li><strong>Via Facebook:</strong> if you connected via Facebook Login, go to Facebook Settings → Apps and Websites → remove 5ads.ai. This automatically triggers a data deletion request to us.</li>
          <li><strong>Contact form:</strong> submit a request through the contact form on 5ads.ai.</li>
        </ul>
        <p>
          Upon receiving a request we will delete your account, connected ad account data, synced leads and
          insights, and generated creatives from our systems within 30 days, and confirm completion by email.
        </p>
      </LegalSection>

      <LegalSection heading="7. Security">
        <p>
          We use industry-standard safeguards including encrypted connections (HTTPS/TLS), encrypted storage
          of access tokens, and access controls limiting who can view your data.
        </p>
      </LegalSection>

      <LegalSection heading="8. Your rights">
        <p>
          Depending on your jurisdiction, you may have the right to access, correct, export, or delete your
          personal data, and to object to or restrict certain processing. To exercise these rights, contact
          us at <a href="mailto:privacy@5ads.ai" className="text-[#FF4D00] underline underline-offset-2">privacy@5ads.ai</a>.
        </p>
      </LegalSection>

      <LegalSection heading="9. Changes to this policy">
        <p>
          We may update this policy from time to time. Material changes will be announced on this page with
          an updated revision date.
        </p>
      </LegalSection>

      <LegalSection heading="10. Contact">
        <p>
          Questions about this policy? Email <a href="mailto:privacy@5ads.ai" className="text-[#FF4D00] underline underline-offset-2">privacy@5ads.ai</a> or
          use the contact form on <a href="/#contact" className="text-[#FF4D00] underline underline-offset-2">5ads.ai</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
